import { MouseEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { OK } from 'http-status-codes';

import Analytics from '@hh.ru/analytics-js';
import { TranslatedComponent } from 'bloko/common/hooks/useTranslations';

import { DeepLink } from 'Utils/DeepLink';
import { getMessengerLinks } from 'Utils/DeepLinkHelper';
import { useNotification } from 'src/components/Notifications/Provider';
import { Messenger } from 'src/components/ResumeCard/Body/Contacts/DeepLinks/types';
import getContactPhones from 'src/components/ResumeView/getContactPhones';
import translation from 'src/components/translation';
import { useSelector } from 'src/hooks/useSelector';

import PhoneDeepLink from 'src/components/ResumeCard/Body/Contacts/DeepLinks/Messengers/Messenger';

const DEEP_LINKS = [Messenger.Whatsapp, Messenger.Viber, Messenger.Telegram] as const;

const Features = {
    warningMessengers: 'warning_messengers',
};

interface Props {
    phone: string;
    resumeId: string;
    resumeHash: string;
    simHash?: string;
    created: number;
    markAsRead?: () => void;
}

const MessengerDeepLinks: TranslatedComponent<Props> = ({
    phone,
    markAsRead,
    resumeId,
    resumeHash,
    simHash,
    created,
}) => {
    const dispatch = useDispatch();
    const { addNotification } = useNotification();

    const displayType = useSelector((state) => state.displayType);
    const vacancyId = useSelector((state) => state.router.location.query?.vacancyId);
    const warningMessengers = (useSelector((state) => state.features[Features.warningMessengers]) as string) || '';

    const messengerLinks = useMemo(() => getMessengerLinks({ phone, displayType }), [displayType, phone]);

    const [messengerType, setMessengerType] = useState<Messenger | null>(null);
    const [isAsyncDeepLink, setAsyncDeepLink] = useState(false);

    const handleDeepLink = useCallback(
        (type: Messenger) => {
            Analytics.sendHHEvent('resumeShowContacts', { goal: type.toUpperCase(), resumeId, vacancyId });
            const { shallow, android, ios, deep } = messengerLinks[type];
            const deepLink = new DeepLink({ fallbackUrl: shallow, androidUrl: android, iosUrl: ios });
            deepLink.open(deep);
        },
        [messengerLinks, resumeId, vacancyId]
    );

    const handlePhoneCheck = useCallback(
        async (type: Messenger, event: MouseEvent) => {
            markAsRead?.();

            if (phone) {
                handleDeepLink(type);
                return;
            }

            let requestResult;
            try {
                requestResult = await getContactPhones(
                    { created, resumeHash, simHash, event, goal: `Contacts_${type}` },
                    addNotification
                )(dispatch);
            } catch (_) {
                return;
            }
            if (requestResult.status === OK) {
                setMessengerType(type);
                setAsyncDeepLink(true);
            }
        },
        [markAsRead, phone, handleDeepLink, created, resumeHash, simHash, addNotification, dispatch]
    );

    useEffect(() => {
        const clearLinkData = () => setMessengerType(null);

        window.addEventListener('blur', clearLinkData);
        return () => window.removeEventListener('blur', clearLinkData);
    }, []);

    useEffect(() => {
        if (isAsyncDeepLink && messengerType) {
            handleDeepLink(messengerType);
            setAsyncDeepLink(false);
        }
    }, [isAsyncDeepLink, messengerType, handleDeepLink]);

    return (
        <>
            {DEEP_LINKS.map((type) => (
                <PhoneDeepLink
                    key={type}
                    type={type}
                    handlePhoneCheck={handlePhoneCheck}
                    isWarning={warningMessengers.includes(type)}
                />
            ))}
        </>
    );
};

export default translation(MessengerDeepLinks);
